import React, { useContext } from 'react'
import styles from '~/styles/page/login/login.module.scss'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

import { LOGIN, LoginResponse, LoginVars } from '~/api/login'

import { useFormErrorState, useFormState } from '~/common/form/formCommon'
import { Cookie, saveCookie } from '~/common/utils/cookie'
import { accountPath, organizationBasePath, resetRequestPath, signupPath } from '~/common/define/path'
import { activityPath } from '~/common/define/activity'
import { LocalStorage, removeStorage } from '~/common/utils/storage'
import { initialData, LoginType, loginValidation } from '~/components/app/form/login/loginType'

import { AppContext } from '~/common/context/AppContext'
import ActionButton from '~/components/atoms/button/ActionButton'
import Form from '~/components/app/form/base/Form'
import FormEmailBox from '~/components/molecules/form/textbox/FormEmailBox'
import FormPassword from '~/components/molecules/form/textbox/FormPassword'
import InternalLink from '~/components/atoms/link/InternalLink'
import LinkText from '~/components/atoms/text/LinkText'
import FormInputNote from '~/components/wrapper/form/FormInputNote'
import { ButtonType } from '~/components/atoms/button/ButtonBase'

const LoginForm = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const context = useContext(AppContext)
  const [formData, setData] = useFormState<LoginType>(initialData)
  const [formError, setError] = useFormErrorState()

  const complete = (data: LoginResponse) => {
    removeStorage(LocalStorage.DATA)
    saveCookie(Cookie.ACCESS_TOKEN, data.login.access_token)
    if (window.location.pathname == '/login') {
      const path = data.login.activity ? activityPath(data.login.activity) : data.login.organization ? organizationBasePath(data.login.organization) : accountPath()
      router.push(path)
    } else {
      router.reload()
    }
  }

  const convertToRequestValues = (type: LoginType): LoginVars => {
    return {
      ...type,
      ip: context.ip,
      user_agent: navigator.userAgent
    }
  }

  return (
    <Form
      mutation={LOGIN}
      complete={complete}
      formData={formData}
      formError={formError}
      setData={setData}
      setError={setError}
      validations={loginValidation}
      isQuery={true}
      convertToRequestValues={convertToRequestValues}
      hideLoading={() => {}}
    >
      <div className={styles.row}>
        <FormEmailBox
          required
          name="email"
          label={t("login:email_address")}
          value={formData.email}
          onChange={setData}
          errors={formError.formWidgetErrors.email}
          onError={setError}
        />
      </div>
      <div className={styles.row}>
        <FormPassword
          required
          name="password"
          label={t("login:password")}
          value={formData.password}
          onChange={setData}
          errors={formError.formWidgetErrors.password}
          onError={setError}
        />
        <FormInputNote>
          <InternalLink link={resetRequestPath()}>
            <LinkText>{t('login:to_password_reset')}</LinkText>
          </InternalLink>
        </FormInputNote>
      </div>
      <div className={styles.text}>
        <p>{t('login:description')}</p>
      </div>
      <div className={styles.bottom}>
        <InternalLink link={signupPath()}>
          <LinkText>{t('login:org_create')}</LinkText>
        </InternalLink>
        <ActionButton type={ButtonType.SUBMIT}>
          {t('login:action')}
        </ActionButton>
      </div>
    </Form>
  )
}

export default LoginForm
